import React, { useEffect, useState, useMemo } from 'react';
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector, useDispatch } from 'react-redux';
import { setbatchId, addAttendence } from 'reducer/attendenceSlice';
import { fetchAttendanceByBatchId } from 'thunks/attendenceSliceThunks/thunks';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { TrainingRequests } from 'requests/TrainingRequests';
import AdditionalDetailsForm from 'Component/MultiStepTrainingForms/TrainingForm/AdditionalDetailsForm';
import Layout from 'Component/Layouts/layout';

export default function FeedbackForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const batchId = useMemo(() => queryParams.get('batchId'), [location]);
  const [activeStep, setActiveStep] = useState(0);
  const [totalMarks, setTotalMarks] = useState('40');
  const [feedbackData, setFeedbackData] = useState([]);
  const [marksError, setMarksError] = useState({});
  const [preAssessmentMarks, setPreAssessmentMarks] = useState({});
  const [postAssessmentMarks, setPostAssessmentMarks] = useState({});
  const [additionalDetails, setAdditionalDetails] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const { data: attendenceData } = useSelector((state) => state.attendenceSlice);

  const handleBackToAttendance = () => {
    navigate(`/attendance/mark?batchId=${batchId}`);
  };

  const handleTotalMarksChange = (e) => {
    const newTotalMarks = e.target.value;
    const marksNum = parseInt(newTotalMarks);

    // Prevent total marks from exceeding 40
    if (marksNum > 40) {
      toast.error('Total marks cannot exceed 40', {
        theme: 'colored',
      });
      return;
    }

    setTotalMarks(newTotalMarks);

    // Validate all existing marks against new total
    const newMarksError = {};
    feedbackData.forEach((feedback) => {
      if (parseInt(feedback.preAssessmentMarks) > marksNum) {
        newMarksError[feedback.empId] =
          `Pre-assessment marks cannot exceed total marks (${marksNum})`;
      }
      if (parseInt(feedback.postAssessmentMarks) > marksNum) {
        newMarksError[feedback.empId] =
          `Post-assessment marks cannot exceed total marks (${marksNum})`;
      }
    });
    setMarksError(newMarksError);
  };

  const handlePreAssessmentMarksChange = (traineeId, newMarks) => {
    const marksNum = parseInt(newMarks);
    const totalMarksNum = parseInt(totalMarks);

    if (marksNum > totalMarksNum) {
      setMarksError((prev) => ({
        ...prev,
        [traineeId]: `Marks cannot exceed total marks (${totalMarksNum})`,
      }));
      return;
    }

    setMarksError((prev) => {
      const newErrors = { ...prev };
      delete newErrors[traineeId];
      return newErrors;
    });

    setPreAssessmentMarks((prev) => ({
      ...prev,
      [traineeId]: newMarks,
    }));

    setFeedbackData((prev) => {
      const existing = prev.findIndex((f) => f.empId === traineeId);
      if (existing >= 0) {
        const newData = [...prev];
        newData[existing] = { ...newData[existing], preAssessmentMarks: newMarks };
        return newData;
      }
      return [...prev, { empId: traineeId, preAssessmentMarks: newMarks }];
    });
  };

  const handlePostAssessmentMarksChange = (traineeId, newMarks) => {
    const marksNum = parseInt(newMarks);
    const totalMarksNum = parseInt(totalMarks);

    if (marksNum > totalMarksNum) {
      setMarksError((prev) => ({
        ...prev,
        [traineeId]: `Marks cannot exceed total marks (${totalMarksNum})`,
      }));
      return;
    }

    setMarksError((prev) => {
      const newErrors = { ...prev };
      delete newErrors[traineeId];
      return newErrors;
    });

    setPostAssessmentMarks((prev) => ({
      ...prev,
      [traineeId]: newMarks,
    }));

    setFeedbackData((prev) => {
      const existing = prev.findIndex((f) => f.empId === traineeId);
      if (existing >= 0) {
        const newData = [...prev];
        newData[existing] = { ...newData[existing], postAssessmentMarks: newMarks };
        return newData;
      }
      return [...prev, { empId: traineeId, postAssessmentMarks: newMarks }];
    });
  };

  const isEmployeeAbsent = (empId) => {
    if (!attendenceData?.data?.attendance) return false;
    let isAbsentInall = true;
    attendenceData.data.attendance.forEach((attendance) => {
      if (!attendance.absent.includes(empId)) {
        isAbsentInall = false;
        return;
      }
    });
    return isAbsentInall;
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      // Validate first step
      if (
        !totalMarks ||
        !attendenceData?.data?.batchTrainees ||
        attendenceData.data.batchTrainees.some((trainee) => {
          const isAbsent = isEmployeeAbsent(trainee.id);
          const feedback = feedbackData.find((f) => f.empId === trainee.id);

          // For absent employees, we only need to check if feedback exists yet
          if (isAbsent) {
            return !feedback?.feedback;
          }

          // For present employees, check all fields
          return (
            !feedback?.preAssessmentMarks ||
            !feedback?.postAssessmentMarks ||
            !feedback?.feedback?.trim() ||
            marksError[trainee.id]
          );
        })
      ) {
        toast.error('Please fill all required fields correctly', {
          theme: 'colored',
        });
        return;
      }
      setActiveStep(1);
    }
  };

  const handleBackStep = () => {
    setActiveStep(0);
  };

  const handleSubmitFeedback = async () => {
    if (!additionalDetails) {
      toast.error('Additional details are required', {
        theme: 'colored',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Create FormData to handle file uploads
      const formData = new FormData();
      formData.append('batchId', batchId);
      formData.append('totalMarks', parseInt(totalMarks));
      formData.append(
        'feedback',
        JSON.stringify(
          feedbackData.map((feedback) => ({
            empId: feedback.empId,
            preAssessmentMarks: parseInt(feedback.preAssessmentMarks),
            postAssessmentMarks: parseInt(feedback.postAssessmentMarks),
            feedback: feedback.feedback,
          }))
        )
      );

      // Append email and thank you notes data
      formData.append('emailRecipients', JSON.stringify(additionalDetails.emailRecipients));
      formData.append('thankYouNotes', JSON.stringify(additionalDetails.thankYouNotes));

      // Append each photo as a separate file
      additionalDetails.photos.forEach((photo, index) => {
        formData.append(`photo${index}`, photo);
      });

      await TrainingRequests.submitFeedback(formData);

      toast.success('Feedback submitted successfully!', {
        theme: 'colored',
      });

      // Navigate back to attendance page
      navigate(`/attendance/mark?batchId=${batchId}`);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(`Failed to submit feedback: ${error.message}`, {
        theme: 'colored',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (batchId) {
      dispatch(setbatchId(batchId));
      dispatch(fetchAttendanceByBatchId(batchId));
    }
  }, [batchId, dispatch]);

  const steps = ['Training Feedback', 'Additional Details'];

  return (
    <Layout>
      <Box sx={{ padding: '20px' }}>
        {/* Header with Back Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            gap: 2,
          }}
        >
          <IconButton
            onClick={handleBackToAttendance}
            sx={{
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Training Feedback - Batch {batchId}
          </Typography>
        </Box>

        {/* Stepper */}
        <Paper sx={{ padding: '20px', marginBottom: '20px' }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                Step 1: Training Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please provide feedback and marks for each trainee. All fields are mandatory.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Total Marks *"
                  type="number"
                  value={totalMarks}
                  onChange={handleTotalMarksChange}
                  required
                  error={!totalMarks || parseInt(totalMarks) > 40}
                  helperText={
                    !totalMarks
                      ? 'Total marks are required'
                      : parseInt(totalMarks) > 40
                        ? 'Total marks cannot exceed 40'
                        : ''
                  }
                  inputProps={{
                    min: 0,
                    max: 40,
                  }}
                  sx={{ maxWidth: 200 }}
                />
              </Box>

              {attendenceData?.data?.batchTrainees && (
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Employee ID
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Pre-Assessment Marks *
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Post-Assessment Marks *
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Feedback *
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendenceData.data.batchTrainees.map((trainee) => {
                        const isAbsent = isEmployeeAbsent(trainee.id);
                        const traineeFeedback =
                          feedbackData.find((f) => f.empId === trainee.id) || {};
                        const marksErrorMessage = marksError[trainee.id];

                        // If employee is absent and no feedback exists yet, set default values
                        if (isAbsent && !traineeFeedback.feedback) {
                          setFeedbackData((prev) => [
                            ...prev,
                            {
                              empId: trainee.id,
                              preAssessmentMarks: 0,
                              postAssessmentMarks: 0,
                              feedback: 'Absent',
                            },
                          ]);
                        }

                        return (
                          <tr
                            key={trainee.id}
                            style={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                          >
                            <td style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>
                              {trainee.id}
                            </td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>
                              {trainee.name}
                            </td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>
                              <TextField
                                size="small"
                                type="number"
                                required
                                disabled={isAbsent}
                                error={!traineeFeedback.preAssessmentMarks || !!marksErrorMessage}
                                helperText={
                                  !traineeFeedback.preAssessmentMarks
                                    ? 'Required'
                                    : marksErrorMessage || ''
                                }
                                value={isAbsent ? 0 : traineeFeedback.preAssessmentMarks || ''}
                                onChange={(e) =>
                                  handlePreAssessmentMarksChange(trainee.id, e.target.value)
                                }
                                inputProps={{
                                  min: 0,
                                  max: totalMarks || 0,
                                  style: { width: '100px' },
                                }}
                              />
                            </td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>
                              <TextField
                                size="small"
                                type="number"
                                required
                                disabled={isAbsent}
                                error={!traineeFeedback.postAssessmentMarks || !!marksErrorMessage}
                                helperText={
                                  !traineeFeedback.postAssessmentMarks
                                    ? 'Required'
                                    : marksErrorMessage || ''
                                }
                                value={isAbsent ? 0 : traineeFeedback.postAssessmentMarks || ''}
                                onChange={(e) =>
                                  handlePostAssessmentMarksChange(trainee.id, e.target.value)
                                }
                                inputProps={{
                                  min: 0,
                                  max: totalMarks || 0,
                                  style: { width: '100px' },
                                }}
                              />
                            </td>
                            <td style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>
                              <TextField
                                size="small"
                                multiline
                                rows={2}
                                required
                                disabled={isAbsent}
                                error={!traineeFeedback.feedback}
                                helperText={!traineeFeedback.feedback ? 'Required' : ''}
                                value={isAbsent ? 'Absent' : traineeFeedback.feedback || ''}
                                onChange={(e) => {
                                  const newFeedback = e.target.value;
                                  setFeedbackData((prev) => {
                                    const existing = prev.findIndex((f) => f.empId === trainee.id);
                                    if (existing >= 0) {
                                      const newData = [...prev];
                                      newData[existing] = {
                                        ...newData[existing],
                                        feedback: newFeedback,
                                      };
                                      return newData;
                                    }
                                    return [...prev, { empId: trainee.id, feedback: newFeedback }];
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                Step 2: Additional Details
              </Typography>
              <AdditionalDetailsForm
                onDataChange={setAdditionalDetails}
                onBack={handleBackStep}
                batchId={batchId}
              />
              {console.log('Passing batchId to AdditionalDetailsForm:', batchId)}
            </>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleBackToAttendance}>
              Cancel
            </Button>
            {activeStep === 0 ? (
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={
                  !totalMarks ||
                  !attendenceData?.data?.batchTrainees ||
                  attendenceData.data.batchTrainees.some((trainee) => {
                    const isAbsent = isEmployeeAbsent(trainee.id);
                    const feedback = feedbackData.find((f) => f.empId === trainee.id);

                    if (isAbsent) {
                      return !feedback?.feedback;
                    }

                    return (
                      !feedback?.preAssessmentMarks ||
                      !feedback?.postAssessmentMarks ||
                      !feedback?.feedback?.trim() ||
                      marksError[trainee.id]
                    );
                  })
                }
              >
                Next
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmitFeedback} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}
